import { getBlogPosts, saveBlogPost, deleteBlogPost } from '../../../lib/cms'

const emptyPost = {
  id: '',
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  category: '',
  category_slug: '',
  image: '',
  date: '',
  author: '',
  author_role: '',
  read_time: '',
  is_published: true,
}

export default function AdminBlog() {
  const [posts, setPosts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [editItem, setEditItem] = useState<any | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [msg, setMsg] = useState('')

  const load = async () => {
    setLoading(true)
    const data = await getBlogPosts()
    // normalize
    setPosts(data.map((p: any) => ({
      ...p,
      category_slug: p.categorySlug ?? p.category_slug,
      author_role: p.authorRole ?? p.author_role,
      read_time: p.readTime ?? p.read_time,
    })))
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 3000) }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editItem) return
    setSaving(true)
    const row = {
      id: editItem.id,
      title: editItem.title,
      slug: editItem.slug,
      excerpt: editItem.excerpt,
      content: editItem.content,
      category: editItem.category,
      category_slug: editItem.category_slug,
      image: editItem.image,
      date: editItem.date,
      author: editItem.author,
      author_role: editItem.author_role,
      read_time: editItem.read_time,
      is_published: editItem.is_published,
    }
    await saveBlogPost(row)
    await load()
    setEditItem(null)
    flash('Blog post sačuvan!')
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!deleteId) return
    await deleteBlogPost(deleteId)
    await load()
    setDeleteId(null)
    flash('Blog post obrisan.')
  }

  const setField = (key: string, value: any) =>
    setEditItem((prev: any) => prev ? { ...prev, [key]: value } : prev)

  return (
    <div className="space-y-4">
      {msg && <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-2 text-sm">{msg}</div>}

      <div className="flex items-center justify-between">
        <p className="text-gray-500 text-sm">{posts.length} blog postova</p>
        <button
          onClick={() => setEditItem({ ...emptyPost, id: `post-${Date.now()}` })}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition"
        >
          <i className="ri-add-line" /> Novi post
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Naslov</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium hidden md:table-cell">Kategorija</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium hidden sm:table-cell">Datum</th>
                <th className="text-center px-4 py-3 text-gray-600 font-medium hidden sm:table-cell">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {posts.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.image && <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />}
                      <div>
                        <p className="font-medium text-gray-900">{p.title}</p>
                        <p className="text-gray-400 text-xs">{p.author}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{p.category}</td>
                  <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{p.date}</td>
                  <td className="px-4 py-3 text-center hidden sm:table-cell">
                    {p.is_published
                      ? <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">Objavljeno</span>
                      : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Draft</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setEditItem({
                        ...p,
                        category_slug: p.categorySlug ?? p.category_slug,
                        author_role: p.authorRole ?? p.author_role,
                        read_time: p.readTime ?? p.read_time,
                      })} className="text-gray-400 hover:text-blue-600 transition p-1"><i className="ri-edit-line" /></button>
                      <button onClick={() => setDeleteId(p.id)} className="text-gray-400 hover:text-red-500 transition p-1"><i className="ri-delete-bin-line" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="font-semibold text-gray-900">Blog post</h2>
              <button onClick={() => setEditItem(null)} className="text-gray-400 hover:text-gray-600"><i className="ri-close-line text-xl" /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Naslov *</label>
                <input className="input" value={editItem.title} onChange={e => setField('title', e.target.value)} required />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL)</label>
                  <input className="input" value={editItem.slug} onChange={e => setField('slug', e.target.value)} placeholder="moj-blog-post" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategorija</label>
                  <input className="input" value={editItem.category} onChange={e => setField('category', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Autor</label>
                  <input className="input" value={editItem.author} onChange={e => setField('author', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Uloga autora</label>
                  <input className="input" value={editItem.author_role} onChange={e => setField('author_role', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
                  <input className="input" value={editItem.date} onChange={e => setField('date', e.target.value)} placeholder="npr. 15. maj 2026." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vreme čitanja</label>
                  <input className="input" value={editItem.read_time} onChange={e => setField('read_time', e.target.value)} placeholder="npr. 5 min" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL slike</label>
                <input className="input" value={editItem.image} onChange={e => setField('image', e.target.value)} placeholder="https://..." />
                {editItem.image && <img src={editItem.image} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kratak opis (excerpt)</label>
                <textarea className="input" rows={2} value={editItem.excerpt} onChange={e => setField('excerpt', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sadržaj (HTML)</label>
                <textarea className="input font-mono text-xs" rows={10} value={editItem.content} onChange={e => setField('content', e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="published" checked={editItem.is_published} onChange={e => setField('is_published', e.target.checked)} className="w-4 h-4 text-blue-600" />
                <label htmlFor="published" className="text-sm text-gray-700">Objavljeno</label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditItem(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Otkaži</button>
                <button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-6 py-2 rounded-lg transition">
                  {saving ? 'Čuvanje...' : 'Sačuvaj'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="font-semibold text-gray-900 mb-2">Obriši blog post?</h3>
            <p className="text-gray-500 text-sm mb-5">Ova akcija je trajna.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm text-gray-600">Otkaži</button>
              <button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition">Obriši</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
